import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Dialog } from '@factorialco/f0-react/dist/experimental'
import { LiveKitRoom, usePersistentUserChoices } from '@livekit/components-react'
import { useMediaQuery } from 'usehooks-ts'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { Drawer } from 'modules/meetings/components/Drawer'
import { RecordSpeakingHandler } from 'modules/meetings/components/RecordSpeakingHandler'
import { SideBar } from 'modules/meetings/components/SideBar'
import { RoomSkeleton } from 'modules/meetings/components/Skeletons/RoomSkeleton'
import { RoomProvider } from 'modules/meetings/lib/context/RoomContext/RoomProvider'
import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'
import { useRoom } from 'modules/meetings/lib/hooks/useRoom'
import { useTranscript } from 'modules/meetings/lib/hooks/useTranscript'

const SIDEBAR_WIDTH = 700

const MeetingRecordContent = () => {
  const { employeeId } = useParams<{ employeeId: string }>()
  const isMobile = useMediaQuery('(max-width: 1024px)')
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { meetingId } = useMeeting()
  const [openEndDialog, setOpenEndDialog] = useState(false)
  useTranscript()

  return (
    <>
      <Box
        paddingX="s24"
        paddingY="s16"
        height="full"
        alignItems="center"
        justifyContent="center"
        position="relative"
        style={
          isMobile
            ? {
                width: '100%',
                height: '50%',
                transition: 'height 0.5s ease-in-out',
                willChange: 'height',
              }
            : {
                width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
                transition: 'width 0.5s ease-in-out',
                willChange: 'width',
              }
        }
      >
        <RecordSpeakingHandler onEndMeeting={() => setOpenEndDialog(true)} />
      </Box>
      <Drawer isMobile={isMobile} isOpen sidebarWidth={SIDEBAR_WIDTH}>
        <SideBar />
      </Drawer>
      <Dialog
        onClose={() => setOpenEndDialog(false)}
        actions={{
          primary: {
            label: i18n.t('meetings.record.end_dialog.button'),
            onClick: () =>
              navigate(
                navigation.engagement.one_on_ones.employeeId.meetingId.fullPath({
                  meetingId: Number(meetingId),
                  employeeId: Number(employeeId),
                })
              ),
            variant: 'critical',
          },
          secondary: {
            label: i18n.t('meetings.record.end_dialog.button_secondary'),
            onClick: () => setOpenEndDialog(false),
          },
        }}
        header={{
          description: i18n.t('meetings.record.end_dialog.description'),
          title: i18n.t('meetings.record.end_dialog.title'),
          type: 'critical',
        }}
        open={openEndDialog}
      />
    </>
  )
}

const MeetingRecordPageContent = () => {
  const { room, loading: loadingRoom } = useRoom()
  const { userChoices } = usePersistentUserChoices()
  const { loading: pageDetailsLoading } = useMeeting()
  const loading = loadingRoom || pageDetailsLoading

  const portalContent = (
    <Box
      position="fixed"
      width="full"
      height="full"
      background={loading ? 'grey000White' : undefined}
      style={{
        background: loading
          ? undefined
          : 'linear-gradient(218.4deg, rgba(255, 95, 90, 0.99) 25.36%, #FF3975 58.18%, #FF0E40 96.61%)',
        zIndex: '999',
        top: '0',
        left: '0',
      }}
    >
      {loading ? (
        <RoomSkeleton />
      ) : (
        <>
          <LiveKitRoom
            serverUrl={room?.serverUrl}
            token={room?.token}
            options={{
              adaptiveStream: true,
              dynacast: true,
            }}
            onConnected={() => {}}
            onError={() => {}}
            audio={{
              deviceId: userChoices?.audioDeviceId,
            }}
            video={false}
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            <MeetingRecordContent />
          </LiveKitRoom>
        </>
      )}
    </Box>
  )

  // Render to main element
  if (typeof document === 'undefined') return portalContent

  const mainElement = document.querySelector('main')
  if (!mainElement) return portalContent

  return createPortal(portalContent, mainElement)
}

const MeetingRecordPage = () => {
  return (
    <RoomProvider inPerson>
      <MeetingRecordPageContent />
    </RoomProvider>
  )
}

export default MeetingRecordPage
